/**
 * fundTermsModel - pure helpers for the Fund terms screen (R4, Friday release
 * 2026-10-09; decision D7). The API is the authority on every rule; these
 * exist so an obviously invalid action never becomes a request and so the
 * screen names a refusal in words.
 *
 * API (ca-rootsfundme-api app/controller/fundTerms.controller.js, AD or CCO):
 *   GET  admin/funds/:campaignId/terms
 *   PUT  admin/funds/:campaignId/terms/confirmation   { fields, source, date }
 *   POST admin/funds/:campaignId/terms/versions       { terms, source, date, reason? }
 *   POST admin/investments/:investmentId/terms-review/ack
 *        { reason, currentTermsVersion, orderTermsVersion }
 */

export const REQUIRED_TERMS = [
  "notes_currency",
  "nominal_per_note",
  "issue_date",
  "min_subscription",
  "min_additional",
  "agreement_key",
  "countersigner_name",
  "countersigner_email",
];

export const TERM_LABELS = {
  notes_currency: "Notes currency",
  nominal_per_note: "Nominal per Note",
  issue_date: "Issue date",
  min_subscription: "Minimum subscription",
  min_additional: "Minimum additional subscription",
  agreement_key: "Subscription agreement",
  countersigner_name: "Countersigner name",
  countersigner_email: "Countersigner e-mail",
};

export const FUND_TERMS_ROLES = ["AD", "CCO"];
export const ACK_REASON_MIN = 20;
export const SOURCE_MIN = 3;

/**
 * The terms the investor's agreement states (the API's
 * fundOffering.AGREEMENT_TERMS). Only a change to one of them is a new terms
 * version, which holds signed unpaid orders (D7); the minimums and the
 * countersigner are recorded on the current version and hold nothing.
 */
export const AGREEMENT_TERMS = ["notes_currency", "nominal_per_note", "issue_date", "agreement_key"];
export const bumpsVersion = (changed) => (changed || []).some((f) => AGREEMENT_TERMS.includes(f));

const MONEY_TERMS = ["nominal_per_note", "min_subscription", "min_additional"];

export const termsUrl = (api, campaignId) => `${api}admin/funds/${campaignId}/terms`;
export const confirmationUrl = (api, campaignId) => `${api}admin/funds/${campaignId}/terms/confirmation`;
export const versionsUrl = (api, campaignId) => `${api}admin/funds/${campaignId}/terms/versions`;
export const ackUrl = (api, investmentId) => `${api}admin/investments/${investmentId}/terms-review/ack`;

const trim = (v) => (v === null || v === undefined ? "" : String(v).trim());

/** Integer cents of a plain amount ("1,000.5" -> 100050), else null. */
function toCents(v) {
  const s = trim(v).replace(/,/g, "");
  const m = /^(\d+)(?:\.(\d+))?$/.exec(s);
  if (!m) return null;
  let frac = m[2] || "";
  if (frac.length > 2) {
    if (/[^0]/.test(frac.slice(2))) return null;
    frac = frac.slice(0, 2);
  }
  return Number(m[1]) * 100 + Number((frac + "00").slice(0, 2));
}

/**
 * The comparison form of a term's value, mirroring the API's
 * normalizeTermValue: money by cents ("1000" = "1000.00"), dates by
 * YYYY-MM-DD, currency and agreement key upper-cased, the e-mail lower-cased,
 * the name with its spaces collapsed; blank is null.
 */
export function normalizeTermValue(field, value) {
  const s = trim(value);
  if (!s) return null;
  if (MONEY_TERMS.includes(field)) {
    const cents = toCents(s);
    return cents === null ? `INVALID:${s}` : String(cents);
  }
  if (field === "issue_date") return s.slice(0, 10);
  if (field === "notes_currency" || field === "agreement_key") return s.toUpperCase();
  if (field === "countersigner_email") return s.toLowerCase();
  if (field === "countersigner_name") return s.replace(/\s+/g, " ");
  return s;
}

/** The terms whose edited value differs from the stored one, in REQUIRED_TERMS order. */
export function changedTerms(current, edited) {
  return REQUIRED_TERMS.filter(
    (f) => normalizeTermValue(f, current && current[f]) !== normalizeTermValue(f, edited && edited[f])
  );
}

/** The terms whose blank is itself a value ("no separate minimum"). */
const BLANK_ALLOWED = ["min_subscription", "min_additional"];

/**
 * Whether a term's CURRENT value can be recorded as confirmed. A blank
 * required term (no countersigner e-mail, no issue date) has nothing for a
 * source to confirm: the API would record CONFIRMED provenance, and its audit
 * row, for a value that does not exist. It is set by a new terms version. A
 * blank minimum is a value ("no separate minimum") and can be confirmed.
 */
export function confirmable(field, value) {
  if (BLANK_ALLOWED.includes(field)) return true;
  return trim(value) !== "";
}

/** The value as the form shows it ("" for null). */
export const formValue = (v) => (v === null || v === undefined ? "" : String(v));

/** A client-side refusal of a provenance record, or null. */
export function validateProvenance({ source, date }, today = new Date().toISOString().slice(0, 10)) {
  if (trim(source).length < SOURCE_MIN) return "Name the source of the confirmation (for example the issuer's letter and its date).";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trim(date))) return "Enter the date of the source.";
  if (trim(date) > today) return "The date of the source cannot be in the future.";
  return null;
}

/**
 * What is wrong with the values a reviewer typed from the source for the
 * selected terms: { missing, differing }, each in REQUIRED_TERMS order.
 * missing: nothing typed for a term that has a stored value (a blank is a
 * statement only where the stored value is blank too: "no separate minimum").
 * differing: the source states another value than the stored one, which is
 * not a confirmation but a new terms version. The API makes the same
 * comparison (409 TERMS_VALUE_DIFFERS); this names it before any request.
 */
export function statedProblems(values, stated, selected) {
  const missing = [];
  const differing = [];
  for (const f of REQUIRED_TERMS) {
    if (!selected.includes(f)) continue;
    const s = trim(stated && stated[f]);
    if (!s && trim(values && values[f])) {
      missing.push(f);
    } else if (normalizeTermValue(f, s) !== normalizeTermValue(f, values && values[f])) {
      differing.push(f);
    }
  }
  return { missing, differing };
}

/**
 * PUT body: for each selected term, the value the SOURCE states, as the
 * reviewer typed it (blank is null), never the stored value read back: the
 * API's "stated value must equal the stored one" check then compares the
 * source with the record, instead of the record with itself.
 */
export function buildConfirmationPayload(stated, selected, source, date) {
  const fields = {};
  for (const f of REQUIRED_TERMS) {
    if (!selected.includes(f)) continue;
    const v = trim(stated && stated[f]);
    fields[f] = v === "" ? null : v;
  }
  return { fields, source: trim(source), date: trim(date) };
}

/**
 * POST body of an acknowledgement: the reason and the two versions the
 * reviewer compared, as the screen shows them (the API refuses 409
 * TERMS_CHANGED when either moved since).
 */
export function buildAckPayload(order, currentTermsVersion, reason) {
  return {
    reason: trim(reason),
    currentTermsVersion,
    orderTermsVersion: order && order.termsVersion !== undefined ? order.termsVersion : null,
  };
}

/**
 * POST body: the CHANGED terms only (blank is null), in REQUIRED_TERMS order.
 * The API records every term it is sent as CONFIRMED from this source, so an
 * unchanged term is not sent: the source would otherwise be stamped on terms
 * it never addressed, and a blank seeded term (a missing countersigner e-mail)
 * would refuse an unrelated change 400 TERMS_VALUE_INVALID. An unchanged
 * SEEDED term stays unconfirmed until a confirmation records it.
 */
export function buildVersionPayload(current, edited, source, date, reason) {
  const terms = {};
  for (const f of changedTerms(current, edited)) {
    const v = trim(edited && edited[f]);
    terms[f] = v === "" ? null : v;
  }
  const body = { terms, source: trim(source), date: trim(date) };
  if (trim(reason)) body.reason = trim(reason);
  return body;
}

const REFUSALS = {
  role_forbidden: "Only the Platform Manager (AD) or the CCO can manage fund terms. The server refused this action.",
  no_role_assigned: "This account holds no admin role, so the server refused this action. Only the Platform Manager (AD) or the CCO can manage fund terms.",
  TERMS_VALUE_DIFFERS: "A stated value differs from the stored one. A confirmation records the current values; to change a term, create a new terms version.",
  TERMS_UNCHANGED: "No term changes value. To record that the current terms are confirmed, use the confirmation instead.",
  TERMS_VALUE_INVALID: "A value is not valid.",
  TERMS_FIELD_UNKNOWN: "A term the server does not know was sent.",
  PROVENANCE_INVALID: "The source or its date is not valid.",
  TERMS_NOT_FOUND: "This fund has no subscription terms yet.",
  NOT_A_FUND: "This campaign is not a fund.",
  CAMPAIGN_NOT_FOUND: "No such campaign.",
  ACK_REASON_REQUIRED: `A reason of at least ${ACK_REASON_MIN} characters is required.`,
  TERMS_REVIEW_NOT_REQUIRED: "This order is not held for a terms review.",
  TERMS_REVIEW_NOT_SIGNED: "This order is not signed: the investor saves the details again under the current terms and signs the current agreement.",
  TERMS_REVIEW_REMINT_REQUIRED: "This order was signed after the terms changed: its agreement must be re-issued under the current terms. A review cannot release it.",
  SELF_REVIEW: "This is your own order: another compliance seat (CCO or AD) must review it.",
  ACK_VERSIONS_REQUIRED: "The acknowledgement must name the versions reviewed. Reload the terms.",
  TERMS_CHANGED: "The terms or the order changed since this screen was loaded. Reload and review again.",
  ORDER_TERMINAL: "This order is cancelled or failed.",
  ALREADY_SETTLED: "This order is already paid.",
};

/**
 * Why a held, signed order is not offered the acknowledgement (the server's
 * ackRefusal for this viewer), in words; null when there is nothing to say.
 */
const ACK_REFUSALS = {
  TERMS_REVIEW_REMINT_REQUIRED: "Signed after the terms changed (on superseded terms): the agreement must be re-issued (voided and re-minted). A review cannot release it.",
  SELF_REVIEW: "Your own order: another compliance seat (CCO or AD) must review it.",
  TERMS_REVIEW_NOT_SIGNED: "Not signed: the investor saves the details again and signs the current agreement.",
};
export const ackRefusalText = (code) => (code ? ACK_REFUSALS[code] || code : null);

/**
 * A refusal in words, ending with the server's code: "<words> (<CODE>)". The
 * server's message wins when it sent one; a role refusal (403) is named here
 * because requireRole sends only its code. With no code the HTTP status
 * stands in ("(HTTP 401)"); with no response at all (network) nothing is
 * invented.
 */
export function describeRefusal(err, fallback = "The action failed.") {
  const response = err && err.response;
  const status = response && response.status;
  const data = (response && response.data) || {};
  const code = data.code || data.error || null;
  let base;
  if (code === "role_forbidden" || code === "no_role_assigned") base = REFUSALS[code];
  else if (status === 403 && !code) base = REFUSALS.role_forbidden;
  else base = data.message || REFUSALS[code] || (err && err.message) || fallback;
  const parts = [base];
  if (Array.isArray(data.differing) && data.differing.length) parts.push(`Differs: ${data.differing.map((f) => TERM_LABELS[f] || f).join(", ")}.`);
  const tag = code || (status ? `HTTP ${status}` : null);
  if (tag) parts.push(`(${tag})`);
  return parts.join(" ");
}

/**
 * How a term's provenance reads. The server's unconfirmed[] decides whether
 * it counts: an entry may claim status CONFIRMED and still not count (no
 * source, no YYYY-MM-DD date or no actor), and the screen must not paint it
 * confirmed while the readiness verdict says it is not.
 */
export function provenanceView(field, entry, unconfirmed) {
  const counted = !(Array.isArray(unconfirmed) && unconfirmed.includes(field));
  if (!entry || !entry.status) return { label: "NONE", counted: false };
  if (entry.status === "CONFIRMED") {
    return counted ? { label: "CONFIRMED", counted: true } : { label: "CONFIRMED (incomplete, not counted)", counted: false };
  }
  return { label: String(entry.status), counted: false };
}

const HOLD_REASONS = {
  REVIEW_PENDING: "Signed before a terms change: awaiting compliance review",
  VERSION_SUPERSEDED: "On a superseded terms version",
  ORDER_VERSION_UNKNOWN: "Details never captured under a terms version",
  CURRENT_VERSION_UNKNOWN: "The fund's terms carry no version",
};
export const holdReasonText = (reason) => (reason ? HOLD_REASONS[reason] || reason : "");

const MISSING = {
  TERMS_VERSION: "terms version",
  COUNTERSIGNER: "countersigner",
  SUBSCRIPTION_DOCUMENT: "subscription agreement template",
  READINESS_CHECK: "readiness check (it failed unexpectedly; nothing is open)",
};

/** The readiness verdict in words, with the server's code: "Not ready (<CODE>): ...". */
export function readinessText(readiness) {
  if (!readiness) return "No terms: the fund is not open.";
  if (readiness.ok) return "Ready: orders, payment and settlement are open on these terms.";
  const head = readiness.code ? `Not ready (${readiness.code}):` : "Not ready:";
  if (readiness.code === "OFFERING_TERMS_NOT_CONFIRMED") {
    return `${head} terms awaiting confirmation (${(readiness.unconfirmed || []).map((f) => TERM_LABELS[f] || f).join(", ")}).`;
  }
  if (readiness.code === "OFFERING_NOT_READY") {
    return `${head} missing ${(readiness.missing || []).map((m) => MISSING[m] || m).join(", ")}.`;
  }
  return `${head} ${readiness.message || readiness.code || "the server gave no reason"}.`;
}
